import {
  add,
  call,
  dependsOn,
  type FnName,
  MINUS_ONE,
  mul,
  type Node,
  neg,
  nodeCount,
  num,
  ONE,
  pow,
  TWO,
  ZERO,
} from "@/lib/tool-kit/calculus/ast";
import { finalize, simplify } from "@/lib/tool-kit/calculus/simplify";

/**
 * Symbolic differentiation with a record of the rule applied at each step.
 *
 * The derivative is computed bottom-up from the standard rules — constant, identity,
 * constant multiple, sum, power, exponential, general power (logarithmic
 * differentiation), product, quotient and chain — and the table of elementary
 * derivatives. Every subexpression it differentiates becomes a `DiffStep` (parents
 * before children), carrying the rule, the expression, and that expression's simplified
 * derivative, so a page can say "product rule on x²·sin x: u′v + uv′ = …".
 */

export type RuleId =
  | "constant"
  | "identity"
  | "constant-multiple"
  | "sum"
  | "power"
  | "power-chain"
  | "exponential"
  | "general-power"
  | "product"
  | "quotient"
  | "chain"
  | "elementary";

export interface DiffStep {
  rule: RuleId;
  /** Nesting level: 0 for the whole expression. */
  depth: number;
  /** The expression being differentiated. */
  target: Node;
  /** Its derivative, simplified. */
  result: Node;
  /** The function, for the chain rule and the elementary table. */
  fn?: FnName;
  /** The pieces the rule names; which are present depends on the rule. */
  parts: {
    /** Constant factor (constant-multiple), exponent (power), or inner function. */
    constant?: Node;
    u?: Node;
    v?: Node;
    uPrime?: Node;
    vPrime?: Node;
    /** The dependent factors of a product, and the derivative of each. */
    factors?: Node[];
    derivatives?: Node[];
    /** f′(u), the derivative of the outer function, for the chain rule. */
    outer?: Node;
  };
}

export interface Derivative {
  /** The derivative in final form. */
  derivative: Node;
  /** First-order working, parents before children. */
  steps: DiffStep[];
}

interface Context {
  v: string;
  steps: DiffStep[];
}

/** Flatten nested sums and products without changing anything else. */
export function tidy(node: Node): Node {
  switch (node.kind) {
    case "add": {
      const terms: Node[] = [];
      for (const term of node.terms.map(tidy)) {
        if (term.kind === "add") {
          terms.push(...term.terms);
        } else {
          terms.push(term);
        }
      }
      return add(...terms);
    }
    case "mul": {
      const factors: Node[] = [];
      for (const factor of node.factors.map(tidy)) {
        if (factor.kind === "mul") {
          factors.push(...factor.factors);
        } else {
          factors.push(factor);
        }
      }
      return mul(...factors);
    }
    case "pow":
      return pow(tidy(node.base), tidy(node.exp));
    case "call":
      return call(node.fn, tidy(node.arg));
    default:
      return node;
  }
}

/** f′(u) for each elementary function. */
function outerDerivative(fn: FnName, u: Node): Node {
  switch (fn) {
    case "sin":
      return call("cos", u);
    case "cos":
      return neg(call("sin", u));
    case "tan":
      return pow(call("sec", u), TWO);
    case "sec":
      return mul(call("sec", u), call("tan", u));
    case "csc":
      return neg(mul(call("csc", u), call("cot", u)));
    case "cot":
      return neg(pow(call("csc", u), TWO));
    case "asin":
      return pow(add(ONE, neg(pow(u, TWO))), num(-1, 2));
    case "acos":
      return neg(pow(add(ONE, neg(pow(u, TWO))), num(-1, 2)));
    case "atan":
      return pow(add(ONE, pow(u, TWO)), MINUS_ONE);
    case "sinh":
      return call("cosh", u);
    case "cosh":
      return call("sinh", u);
    case "tanh":
      return pow(call("cosh", u), num(-2));
    case "ln":
      return pow(u, MINUS_ONE);
    case "log":
      return mul(pow(u, MINUS_ONE), pow(call("ln", num(10)), MINUS_ONE));
    default:
      return mul(u, pow(call("abs", u), MINUS_ONE));
  }
}

function record(
  ctx: Context,
  depth: number,
  rule: RuleId,
  target: Node,
  extra: Pick<DiffStep, "fn" | "parts">,
  compute: (parts: DiffStep["parts"]) => Node
): Node {
  const step: DiffStep = { rule, depth, target, result: ZERO, ...extra };
  ctx.steps.push(step);
  step.result = simplify(compute(step.parts));
  return step.result;
}

function isDenominator(factor: Node): boolean {
  return factor.kind === "pow" && factor.exp.kind === "num" && factor.exp.n < 0;
}

function joinFactors(factors: Node[]): Node {
  return factors.length === 1 ? factors[0] : mul(...factors);
}

function differentiateProduct(
  node: Extract<Node, { kind: "mul" }>,
  ctx: Context,
  depth: number
): Node {
  const constants = node.factors.filter((f) => !dependsOn(f, ctx.v));
  const dependent = node.factors.filter((f) => dependsOn(f, ctx.v));
  if (constants.length > 0) {
    const c = joinFactors(constants);
    const g = joinFactors(dependent);
    return record(
      ctx,
      depth,
      "constant-multiple",
      node,
      { parts: { constant: c, u: g } },
      (parts) => {
        parts.uPrime = differentiate1(g, ctx, depth + 1);
        return mul(c, parts.uPrime);
      }
    );
  }
  const denominators = dependent.filter(isDenominator);
  if (denominators.length > 0) {
    const numerator = dependent.filter((f) => !isDenominator(f));
    const top = numerator.length === 0 ? ONE : joinFactors(numerator);
    const bottom = joinFactors(
      denominators.map((f) => {
        if (f.kind !== "pow" || f.exp.kind !== "num") {
          return f;
        }
        return f.exp.n === -f.exp.d
          ? f.base
          : pow(f.base, num(-f.exp.n, f.exp.d));
      })
    );
    return record(
      ctx,
      depth,
      "quotient",
      node,
      { parts: { u: top, v: bottom } },
      (parts) => {
        parts.uPrime = differentiate1(top, ctx, depth + 1);
        parts.vPrime = differentiate1(bottom, ctx, depth + 1);
        return mul(
          add(mul(parts.uPrime, bottom), neg(mul(top, parts.vPrime))),
          pow(bottom, num(-2))
        );
      }
    );
  }
  const [first, ...others] = dependent;
  const second = joinFactors(others);
  return record(
    ctx,
    depth,
    "product",
    node,
    { parts: { u: first, v: second, factors: dependent } },
    (parts) => {
      const derivatives = dependent.map((f) =>
        differentiate1(f, ctx, depth + 1)
      );
      parts.derivatives = derivatives;
      parts.uPrime = derivatives[0];
      parts.vPrime = dependent.length === 2 ? derivatives[1] : undefined;
      return add(
        ...dependent.map((_, i) =>
          mul(derivatives[i], ...dependent.filter((__, j) => j !== i))
        )
      );
    }
  );
}

function differentiatePower(
  node: Extract<Node, { kind: "pow" }>,
  ctx: Context,
  depth: number
): Node {
  const { base, exp } = node;
  const baseDepends = dependsOn(base, ctx.v);
  const expDepends = dependsOn(exp, ctx.v);
  if (!expDepends) {
    const lowered = pow(base, simplify(add(exp, MINUS_ONE)));
    if (base.kind === "var") {
      return record(
        ctx,
        depth,
        "power",
        node,
        { parts: { constant: exp, u: base } },
        () => mul(exp, lowered)
      );
    }
    return record(
      ctx,
      depth,
      "power-chain",
      node,
      { parts: { constant: exp, u: base } },
      (parts) => {
        parts.uPrime = differentiate1(base, ctx, depth + 1);
        return mul(exp, lowered, parts.uPrime);
      }
    );
  }
  if (!baseDepends) {
    return record(
      ctx,
      depth,
      "exponential",
      node,
      { parts: { constant: base, u: exp } },
      (parts) => {
        parts.uPrime = differentiate1(exp, ctx, depth + 1);
        return mul(node, call("ln", base), parts.uPrime);
      }
    );
  }
  return record(
    ctx,
    depth,
    "general-power",
    node,
    { parts: { u: base, v: exp } },
    (parts) => {
      parts.uPrime = differentiate1(base, ctx, depth + 1);
      parts.vPrime = differentiate1(exp, ctx, depth + 1);
      return mul(
        node,
        add(
          mul(parts.vPrime, call("ln", base)),
          mul(exp, parts.uPrime, pow(base, MINUS_ONE))
        )
      );
    }
  );
}

function differentiateCall(
  node: Extract<Node, { kind: "call" }>,
  ctx: Context,
  depth: number
): Node {
  const outer = outerDerivative(node.fn, node.arg);
  if (node.arg.kind === "var") {
    return record(
      ctx,
      depth,
      "elementary",
      node,
      { fn: node.fn, parts: { u: node.arg } },
      (parts) => {
        parts.outer = simplify(outer);
        return outer;
      }
    );
  }
  return record(
    ctx,
    depth,
    "chain",
    node,
    { fn: node.fn, parts: { u: node.arg } },
    (parts) => {
      parts.outer = simplify(outer);
      parts.uPrime = differentiate1(node.arg, ctx, depth + 1);
      return mul(outer, parts.uPrime);
    }
  );
}

function differentiate1(node: Node, ctx: Context, depth: number): Node {
  if (!dependsOn(node, ctx.v)) {
    return record(ctx, depth, "constant", node, { parts: {} }, () => ZERO);
  }
  switch (node.kind) {
    case "var":
      return record(ctx, depth, "identity", node, { parts: {} }, () => ONE);
    case "add":
      return record(ctx, depth, "sum", node, { parts: {} }, () =>
        add(...node.terms.map((t) => differentiate1(t, ctx, depth + 1)))
      );
    case "mul":
      return differentiateProduct(node, ctx, depth);
    case "pow":
      return differentiatePower(node, ctx, depth);
    case "call":
      return differentiateCall(node, ctx, depth);
    default:
      return ZERO;
  }
}

/** d/dv of an expression, with the first-order working. */
export function differentiate(ast: Node, v: string): Derivative {
  const ctx: Context = { v, steps: [] };
  const raw = differentiate1(tidy(ast), ctx, 0);
  return { derivative: finalize(raw), steps: ctx.steps };
}

/** Past this many nodes a higher derivative is no longer worth showing. */
export const MAX_DERIVATIVE_NODES = 4000;

/**
 * The derivative of each order 1…n, each in final form. Stops early (returning fewer
 * than n) once an expression grows past `maxNodes`, which only happens for deeply
 * nested products and compositions.
 */
export function derivativesUpTo(
  ast: Node,
  v: string,
  order: number,
  maxNodes = MAX_DERIVATIVE_NODES
): Node[] {
  const out: Node[] = [];
  let current = ast;
  for (let k = 0; k < order; k += 1) {
    const ctx: Context = { v, steps: [] };
    current = finalize(differentiate1(tidy(current), ctx, 0));
    out.push(current);
    if (nodeCount(current) > maxNodes) {
      break;
    }
  }
  return out;
}
